import { Alert } from '@tempmee/react';
import { forwardRef, type CSSProperties, type ReactNode } from 'react';

export interface IFormErrorBannerProps {
  visible: boolean;
  /** Single message or list of messages. When multiple, each renders on its own line. */
  message?: string | string[];
  /**
   * Short prefix rendered before the message. Pass an empty string to omit.
   * @default 'Oops!'
   */
  title?: string;
  /** Extra Tailwind classes forwarded to the Alert. */
  className?: string;
  /** Inline styles forwarded to the Alert (use to override CVA padding, e.g. `{ paddingLeft: 0, paddingRight: 0 }`). */
  ribbonStyle?: CSSProperties;
  /** @default 'form-error-banner' */
  'data-testid'?: string;
}

const DEFAULT_MESSAGE = 'It looks like a few required fields were left empty.';
const DEFAULT_TITLE = 'Oops!';

const normalizeMessages = (message: string | string[] | undefined): string[] => {
  if (Array.isArray(message)) {
    const cleaned = message.filter((m): m is string => Boolean(m));
    return cleaned.length > 0 ? cleaned : [DEFAULT_MESSAGE];
  }
  return [message ?? DEFAULT_MESSAGE];
};

const renderContent = (messages: string[], title: string): ReactNode => {
  if (messages.length <= 1) {
    const single = messages[0] ?? DEFAULT_MESSAGE;
    return title ? `${title} ${single}` : single;
  }
  return (
    <>
      {title && <span style={{ display: 'block' }}>{title}</span>}
      {messages.map((m, i) => (
        <span key={i} style={{ display: 'block' }}>
          {m}
        </span>
      ))}
    </>
  );
};

/**
 * Standardized error banner for form screens.
 * Renders the design-system `Alert` with `severity="error"`.
 * Supports `forwardRef` so the parent can scroll it into view.
 */
export const FormErrorBanner = forwardRef<HTMLDivElement, IFormErrorBannerProps>(
  (
    {
      visible,
      message,
      title = DEFAULT_TITLE,
      className,
      ribbonStyle,
      'data-testid': testId = 'form-error-banner',
    },
    ref,
  ) => {
    if (!visible) return null;
    const messages = normalizeMessages(message);
    const content = renderContent(messages, title);
    return (
      <div ref={ref} role="alert" aria-live="polite" data-testid={testId}>
        <Alert
          severity="error"
          className={className}
          style={ribbonStyle}
          description={content}
        />
      </div>
    );
  },
);

FormErrorBanner.displayName = 'FormErrorBanner';
